import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { config } from '../configs/app.config';
import { connect } from 'react-redux';
import {
  setOrder,
  setSharedCarts,
  setStoreSpecificOrder,
  setTableCart,
  addProcessedSocketEventId,
} from '../redux/store/store.actions';
import { setSpecificOrder } from '../redux/me/me.actions';
import {
  handleExpectedTimeUpdateNotification,
  handleOrderUpdateNotification,
  notificationFormattedMessage,
} from '../utility/notification/notification.service';
import retry from 'async-retry';
import {
  setOrderInfoModalData,
  setIsOrderInfoModalOpen,
} from '../redux/modal/modal.actions';
import {
  getCustomerOrder,
  getStoreOrder,
} from '../utility/sb-api/sb-api.service';
import {
  CHECKOUT_METHOD_PAY_AND_GO,
  ORDER_STATUS_PREPARING,
  ORDER_TYPE_DELIVERY,
  ORDER_TYPE_PICKUP,
} from '../utility/constants';
import {
  convertCartToOrderItems,
  isStoreOwner,
} from '../utility/util/util.service';
import { debounce } from 'debounce';

const Context = React.createContext({});

const SocketProviderWrapper = ({
  me,
  children,
  table,
  cart,
  checkout,
  orders,
  order,
  storeOrders,
  pickupStation,
  processedSocketEventIds,
  store,

  setTableCart,
  setSpecificOrder,
  setStoreSpecificOrder,
  setSharedCarts,
  setOrderInfoModalData,
  setIsOrderInfoModalOpen,
  setOrder,
  addProcessedSocketEventId,
}) => {
  const socket = useRef(null);
  const [token, setToken] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [hasJoinedMerchantProfile, setHasJoinedMerchantProfile] = useState(
    false,
  );
  const [hasJoinedCustomerProfile, setHasJoinedCustomerProfile] = useState(
    false,
  );
  const [hasJoinedStore, setHasJoinedStore] = useState(false);
  const [hasJoinedTable, setHasJoinedTable] = useState(false);
  const [hasJoinedPickupStation, setHasJoinedPickupStation] = useState(false);
  const cartQty = useRef(0);
  const ordersRef = useRef(orders); // hold the latest orders state
  const storeOrdersRef = useRef(storeOrders); // hold the latest store orders state
  const orderRef = useRef(order); // hold the latest currently-viewed single order
  const tableCartUpdateDebouncer = useRef(null);
  const processedEventIdsRef = useRef(processedSocketEventIds);
  const meRef = useRef(me); // hold the latest me state
  // Tracks whether the socket has connected at least once for this token, so
  // we can distinguish initial connect (no reconcile needed) from reconnect
  // (refetch authoritative state via REST if the server-side Connection State
  // Recovery window was exceeded). Reset every time the effect tears down.
  const hasConnectedBeforeRef = useRef(false);

  // Update the ordersRef whenever orders changes
  useEffect(() => {
    ordersRef.current = orders || [];
  }, [orders]);

  // Update the orderRef whenever the current checkout order changes.
  // The socket 'order' handler is registered once (deps: [token]), so it must
  // read the live order through this ref instead of the captured prop.
  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  // Update the storeOrdersRef whenever storeOrders changes
  useEffect(() => {
    storeOrdersRef.current = storeOrders || [];
  }, [storeOrders]);

  // Update the processedEventIdsRef whenever processedSocketEventIds changes
  useEffect(() => {
    processedEventIdsRef.current = processedSocketEventIds || [];
  }, [processedSocketEventIds]);

  // Update the meRef whenever me changes
  useEffect(() => {
    meRef.current = me;
  }, [me]);

  useEffect(() => {
    if (token) {
      socket.current = io(config.wsBaseUrl, {
        transports: ['websocket'],
        auth: { token },
        reconnection: true,
        autoConnect: true,
      });

      socket.current.on('connect', () => {
        const isReconnect = hasConnectedBeforeRef.current;
        const recovered = socket.current?.recovered ?? false;
        hasConnectedBeforeRef.current = true;

        console.info(
          `Connected to Socket! (reconnect=${isReconnect}, recovered=${recovered})`,
        );
        setIsSocketConnected(true);

        // Long-disconnect reconciliation. Server-side Connection State
        // Recovery (see sb-bend) replays missed messages for short blips;
        // 'socket.recovered === true' means we got that replay and don't
        // need to refetch. Anything else after a reconnect means we may
        // have missed broadcasts — pull the authoritative state via REST.
        if (isReconnect && !recovered) {
          reconcileStateAfterReconnect();
        }
      });

      socket.current.on('connect_error', () => {
        console.info('Cannot connect to Socket, retrying...');
      });

      socket.current.on('error', (err) => {
        console.info('Socket Error:', err.message);
      });

      socket.current.on('disconnect', (reason) => {
        console.info(`Disconnected from Socket (${reason})`);
        setIsSocketConnected(false);
        setHasJoinedMerchantProfile(false);
        setHasJoinedCustomerProfile(false);
        setHasJoinedStore(false);
        setHasJoinedTable(false);
        setHasJoinedPickupStation(false);
      });

      socket.current.on('order', async (message, ack) => {
        if (typeof ack === 'function') ack('OK'); // acknowledge event if callback provided

        // Universal handler: supports both old and new message formats
        // Old format: {to: "...", order: {...}}
        // New format: {id: "...", seq: ..., data: {to: "...", order: {...}}}
        // Or: {id: "...", seq: ..., data: {order object with id, status, etc}}
        const payload = message?.data || message;

        let updatedOrder =
          payload?.order || (payload?.id && payload?.status ? payload : null);

        const eventId = message?.id;
        const orderId = updatedOrder?.id || message?.id;

        // Skip if we've already processed this event
        if (eventId && processedEventIdsRef.current.includes(eventId)) {
          console.info(`Skipping duplicate event (id=${eventId})`);
          return;
        }

        // Store the event ID
        if (eventId) {
          addProcessedSocketEventId(eventId);
        }

        // If no order data in message, try to find it in Redux
        if (!updatedOrder && orderId) {
          console.info(
            `Order data not in message, searching Redux for order ${orderId}`,
          );

          // Search in customer orders first
          const orderInCustomerOrders = ordersRef.current?.find(
            (o) => o.id === orderId,
          );

          // Search in store orders if not found in customer orders
          const orderInStoreOrders = storeOrdersRef.current?.find(
            (o) => o.id === orderId,
          );

          updatedOrder = orderInCustomerOrders || orderInStoreOrders;

          // If still not found in Redux, fetch from API
          if (!updatedOrder) {
            console.info(
              `Order ${orderId} not found in Redux, fetching from API`,
            );
            try {
              // Determine if we should fetch as merchant or customer
              const isMerchant =
                isStoreOwner(meRef.current) ||
                meRef.current?.isAdmin ||
                meRef.current?.requester?.employeeProfile ||
                meRef.current?.onBehalfOfStore;

              if (isMerchant) {
                updatedOrder = await getStoreOrder(orderId);
              } else {
                updatedOrder = await getCustomerOrder(orderId);
              }

              console.info(`Successfully fetched order ${orderId} from API`);
            } catch (error) {
              console.error(
                `Failed to fetch order ${orderId} from API:`,
                error,
              );
              return;
            }
          } else {
            console.info(`Found order ${orderId} in Redux`);
          }
        }

        if (!updatedOrder) {
          console.warn(
            'Received order event without order data and could not fetch:',
            message,
          );
          return;
        }

        // Check if we're on a pickup station route - these are always merchant-facing
        const isOnPickupStationRoute = window.location.pathname.includes(
          '/pickup-station/',
        );

        // Determine if current user is merchant or customer
        const isMerchant =
          isStoreOwner(meRef.current) ||
          meRef.current?.isAdmin ||
          meRef.current?.requester?.employeeProfile ||
          meRef.current?.onBehalfOfStore;

        console.log(
          'ORDER EVENT - isMerchant:',
          isMerchant,
          'isOnPickupStationRoute:',
          isOnPickupStationRoute,
          'me:',
          meRef.current,
          'isStoreOwner:',
          isStoreOwner(meRef.current),
          'isAdmin:',
          meRef.current?.isAdmin,
          'hasEmployeeProfile:',
          !!meRef.current?.requester?.employeeProfile,
          'hasOnBehalfOfStore:',
          !!meRef.current?.onBehalfOfStore,
        );

        // Handle based on message format (old format has 'to' field, new format doesn't)
        // Pickup station routes are always merchant-facing regardless of user type
        const isForCustomer =
          !isOnPickupStationRoute &&
          (payload?.to === 'CustomerProfile' || (!payload?.to && !isMerchant));
        const isForMerchant =
          isOnPickupStationRoute ||
          payload?.to === 'MerchantProfile' ||
          (!payload?.to && isMerchant);

        console.log(
          'ORDER EVENT - isForCustomer:',
          isForCustomer,
          'isForMerchant:',
          isForMerchant,
        );

        if (isForCustomer) {
          console.log(
            'ORDER EVENT - Calling setSpecificOrder for CustomerProfile',
          );
          handleOrderUpdateNotification(updatedOrder, ordersRef.current);
          handleExpectedTimeUpdateNotification(updatedOrder, ordersRef.current);
          setSpecificOrder(updatedOrder); // updates the array of all Orders (User)

          // update the order while in checkout, but only if it is the same Order!
          // Read the live order via ref: this handler is registered once, so the
          // captured `order` prop is stale (null at connect time).
          if (orderRef.current?.id === updatedOrder?.id) setOrder(updatedOrder);
        }

        if (isForMerchant) {
          console.log(
            'ORDER EVENT - Calling setStoreSpecificOrder for MerchantProfile',
          );

          // Show notification for order status changes
          handleOrderUpdateNotification(updatedOrder, storeOrdersRef.current);

          // Open modal for all merchant order updates (except on pickup station routes)
          // Pickup station routes handle their own modal opening with pickupStationId filtering
          if (!isOnPickupStationRoute) {
            console.log(
              'SOCKET - Opening order info modal for merchant order update',
            );
            setOrderInfoModalData({ order: updatedOrder, isAdminMode: true });
            setIsOrderInfoModalOpen(true);
          } else {
            console.log(
              'SOCKET - On pickup station route, skipping auto-open of modal',
            );
          }

          console.log(
            'ORDER EVENT - About to call setStoreSpecificOrder with order:',
            updatedOrder.id,
            'status:',
            updatedOrder.status,
          );
          setStoreSpecificOrder(updatedOrder); // updates the array of all Orders (Store)
          console.log('ORDER EVENT - Called setStoreSpecificOrder');
        }
      });

      socket.current.on('scanner/action', async (message, ack) => {
        if (typeof ack === 'function') ack('OK'); // acknowledge event if callback provided

        // Universal handler: supports both old and new message formats
        // Old format: {payload: {...}, action: "...", pickupStationId: "..."}
        // New format: {id: "...", seq: ..., data: {payload: {...}, action: "...", pickupStationId: "..."}}
        const payload = message?.data || message;
        const { payload: actionPayload, action, pickupStationId } = payload;
        const eventId = message?.id;

        // Skip if we've already processed this event
        if (eventId && processedEventIdsRef.current.includes(eventId)) {
          console.info(
            `Skipping duplicate scanner/action event (id=${eventId})`,
          );
          return;
        }

        // Store the event ID
        if (eventId) {
          addProcessedSocketEventId(eventId);
        }

        // Since we're subscribed to a specific pickup station, all events are for this station
        // No need to check if we're on the matching route - if we're subscribed, we want the events

        // Handle error cases
        if (actionPayload?.error && pickupStationId) {
          console.error(
            `Scanner action error at pickup station ${pickupStationId}:`,
            actionPayload.error,
          );

          // Check if we're on a pickup station route to show notification
          const currentPath = window.location.pathname;
          const isOnPickupStationRoute = currentPath.includes(
            '/pickup-station/',
          );

          if (isOnPickupStationRoute) {
            // Determine if it's store or customer view
            const isStoreView = currentPath.includes('/store');
            const autoClose = isStoreView ? 15000 : 8000; // 15 seconds for store, 8 seconds for customer

            // Import at top of file is needed, but we can use dynamic import here
            import('../utility/notification/notification.service').then(
              ({ notificationFormattedMessage }) => {
                notificationFormattedMessage(
                  'error',
                  actionPayload.error === 'Invalid Payload'
                    ? 'scannerInvalidPayloadError'
                    : 'scannerOrderNotFoundLabel',
                  {},
                  autoClose,
                  `scanner-error-${pickupStationId}-${Date.now()}`,
                );
              },
            );
          }
          return;
        }

        if (!actionPayload || !action) {
          console.warn(
            'Received scanner/action event with missing data:',
            message,
          );
          return;
        }

        //in this case actionPayload contains order ID
        if (['order-info', 'order-completed'].includes(action)) {
          const orderId = actionPayload?.id;

          if (!orderId) {
            console.warn(
              'Received scanner/action event without order ID:',
              message,
            );
            return;
          }

          console.info(
            `Received scanner action "${action}" for order ${orderId} (pickupStationId=${pickupStationId})`,
          );

          // Search for order in Redux first
          let order = ordersRef.current?.find((o) => o.id === orderId);

          if (!order) {
            order = storeOrdersRef.current?.find((o) => o.id === orderId);
          }

          // If not found in Redux, fetch from API
          if (!order) {
            console.info(
              `Order ${orderId} not found in Redux, fetching from API`,
            );

            try {
              // Always use store order endpoint since pickup station is merchant-facing
              // (even in customer view, it's a merchant viewing it)
              order = await getStoreOrder(
                orderId,
                '1bf06b3b-9f7c-4753-9bfc-b51f16c16fd0',
              );

              console.info(`Successfully fetched order ${orderId} from API`);
            } catch (error) {
              console.error(
                `Failed to fetch order ${orderId} from API:`,
                error,
              );
              return;
            }
          } else {
            console.info(`Found order ${orderId} in Redux`);
          }

          // Determine isAdminMode based on the current route
          const currentPath = window.location.pathname;
          const isAdminMode = currentPath.includes('/store');

          // Add the order to Redux so it's available for updates
          if (isAdminMode) {
            setStoreSpecificOrder(order);
          } else {
            setSpecificOrder(order);
          }

          // Set order data - the pickup station component will handle displaying it
          setOrderInfoModalData({
            order,
            pickupStationId,
            isAdminMode,
          });

          // For store view, open the modal immediately
          if (isAdminMode) {
            setIsOrderInfoModalOpen(true);
          }
          // For customer view, the pickup station component handles the display
        }
      });

      socket.current.on('table/shared-carts', (message, ack) => {
        if (typeof ack === 'function') ack('OK'); // acknowledge event if callback provided

        // Universal handler: supports both old and new message formats
        // Old format: [{...}, {...}] (array of carts)
        // New format: {id: "...", seq: ..., data: [{...}, {...}]}
        const carts = message?.data || message;
        const eventId = message?.id;

        // Skip if we've already processed this event
        if (eventId && processedEventIdsRef.current.includes(eventId)) {
          console.info(
            `Skipping duplicate table/shared-carts event (id=${eventId})`,
          );
          return;
        }

        // Store the event ID
        if (eventId) {
          addProcessedSocketEventId(eventId);
        }

        console.info('Received SharedCarts update', carts);
        setSharedCarts(carts);
      });

      socket.current.on('table/cart', (message, ack) => {
        if (typeof ack === 'function') ack('OK'); // acknowledge event if callback provided

        // Universal handler: supports both old and new message formats
        // Old format: {...} (cart object)
        // New format: {id: "...", seq: ..., data: {...}}
        const tableCart = message?.data || message;
        const eventId = message?.id;

        // Skip if we've already processed this event
        if (eventId && processedEventIdsRef.current.includes(eventId)) {
          console.info(`Skipping duplicate table/cart event (id=${eventId})`);
          return;
        }

        // Store the event ID
        if (eventId) {
          addProcessedSocketEventId(eventId);
        }

        console.info('Received TableCart update', tableCart);
        if (tableCartUpdateDebouncer.current) {
          tableCartUpdateDebouncer.current.clear();
        }
        tableCartUpdateDebouncer.current = debounce(() => {
          setTableCart(tableCart);
        }, 300);
        tableCartUpdateDebouncer.current();
      });

      socket.current.on('store/update', (message) => {
        // Only customers receive this event (they subscribe via store/join)
        console.info('Store has been updated! Reloading page...', message);

        // Show notification before reload
        notificationFormattedMessage('info', 'storeIsReloadingText', {}, 3000);

        // Reload the page after a short delay to allow notification to show
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      });

      /*   socket.current.on('kiosk/reload', (kiosk, ack) => {
        ack('OK'); // acknowledge event first
        window.location.href = window.location.href;
      }); */
    }

    return () => {
      if (socket.current?.connected) {
        socket.current.disconnect();
      }
      // New token / unmount: next mount is a fresh lifecycle, so the next
      // connect is initial and must NOT trigger reconcile.
      hasConnectedBeforeRef.current = false;
    };
  }, [token]);

  /**
   * Refetch authoritative state via REST after a long-disconnect reconnect
   * (server-side Connection State Recovery window was exceeded). Each
   * downstream `set*` action runs through Redux so views update naturally.
   *
   * We deliberately scope this to what the user is currently looking at —
   * blanket-refetching every list on every reconnect would be wasteful and
   * could trample optimistic updates.
   */
  const reconcileStateAfterReconnect = async () => {
    const currentMe = meRef.current;
    const currentOrder = orderRef.current;
    const isMerchant =
      isStoreOwner(currentMe) ||
      currentMe?.isAdmin ||
      currentMe?.requester?.employeeProfile ||
      currentMe?.onBehalfOfStore;

    try {
      // 1. Refresh the currently-displayed single order, if any. This is
      //    the most operationally important state on customer order-status
      //    and merchant order-detail pages — a missed status broadcast
      //    leaves the user staring at a stale order.
      if (currentOrder?.id) {
        const fresh = isMerchant
          ? await getStoreOrder(currentOrder.id)
          : await getCustomerOrder(currentOrder.id);
        if (fresh) {
          // Only adopt if not strictly older than what we already have —
          // protects against an out-of-order REST response landing after a
          // fresh websocket update.
          if (
            !currentOrder.updatedAt ||
            !fresh.updatedAt ||
            fresh.updatedAt >= currentOrder.updatedAt
          ) {
            if (isMerchant) {
              setStoreSpecificOrder(fresh);
            } else {
              setSpecificOrder(fresh);
            }
            console.info(
              `[Socket] Reconcile applied REST snapshot for order ${currentOrder.id}`,
            );
          }
        }
      }
    } catch (err) {
      // Reconcile is best-effort — websocket events will keep flowing and
      // background polling on the relevant pages picks up the slack.
      console.warn('[Socket] reconcileStateAfterReconnect failed:', err);
    }
  };

  // Join customer-profile for customers (to receive their own order updates)
  useEffect(() => {
    if (isSocketConnected && me && !hasJoinedCustomerProfile) {
      // Determine if current user is merchant (store owner/employee/admin on behalf of store)
      const isMerchant =
        isStoreOwner(me) ||
        me?.isAdmin ||
        me?.requester?.employeeProfile ||
        me?.onBehalfOfStore;

      // Only customers join customer-profile
      if (!isMerchant) {
        retry(() =>
          socket.current.timeout(2000).emitWithAck('customer-profile/join'),
        )
          .then((msg) => {
            console.info(`Joined CustomerProfile (${msg})`);
            setHasJoinedCustomerProfile(true);
          })
          .catch((err) => {
            console.error('Failed to join CustomerProfile:', err);
          });
      }
    }
  }, [isSocketConnected, hasJoinedCustomerProfile, me]);

  // Join store-manager for merchants when store is available (to receive store order updates)
  useEffect(() => {
    if (isSocketConnected && me && store && !hasJoinedMerchantProfile) {
      // Determine if current user is merchant (store owner/employee/admin on behalf of store)
      const isMerchant =
        isStoreOwner(me) ||
        me?.isAdmin ||
        me?.requester?.employeeProfile ||
        me?.onBehalfOfStore;

      // Only merchants join store-manager
      if (isMerchant) {
        const joinStoreManagerPayload = { storeId: store.id };
        retry(() =>
          socket.current
            .timeout(2000)
            .emitWithAck('store-manager/join', joinStoreManagerPayload),
        )
          .then((msg) => {
            console.info(
              `Joined StoreManager for "${store.name}" (${msg})`,
              joinStoreManagerPayload,
            );
            setHasJoinedMerchantProfile(true);
          })
          .catch((err) => {
            console.error('Failed to join StoreManager:', err);
          });
      }
    }
  }, [isSocketConnected, hasJoinedMerchantProfile, me, store]);

  // Leave store-manager or customer-profile on disconnect or user type change
  useEffect(() => {
    return () => {
      if (socket.current?.connected) {
        if (hasJoinedMerchantProfile) {
          socket.current.emit('store-manager/leave');
          console.info('Left StoreManager');
        }
        if (hasJoinedCustomerProfile) {
          socket.current.emit('customer-profile/leave');
          console.info('Left CustomerProfile');
        }
      }
    };
  }, [hasJoinedMerchantProfile, hasJoinedCustomerProfile]);

  // Join store-user when store is available (only for customers, to receive store updates like menu changes)
  useEffect(() => {
    if (
      isSocketConnected &&
      !hasJoinedStore &&
      store &&
      hasJoinedCustomerProfile
    ) {
      const joinStorePayload = { storeId: store.id };
      retry(() =>
        socket.current
          .timeout(2000)
          .emitWithAck('store-user/join', joinStorePayload),
      ).then((msg) => {
        console.info(`Joined Store "${store.name}" (${msg})`, joinStorePayload);
        setHasJoinedStore(true);
      });
    }
  }, [isSocketConnected, hasJoinedStore, store, hasJoinedCustomerProfile]);

  // Leave store-user when store is no longer available (only for customers)
  useEffect(() => {
    if (
      isSocketConnected &&
      hasJoinedStore &&
      !store &&
      hasJoinedCustomerProfile
    ) {
      retry(() =>
        socket.current.timeout(2000).emitWithAck('store-user/leave'),
      ).then((msg) => {
        console.info(`Left Store (${msg})`);
        setHasJoinedStore(false);
      });
    }
  }, [isSocketConnected, hasJoinedStore, store, hasJoinedCustomerProfile]);

  // join-table process
  useEffect(() => {
    if (
      isSocketConnected &&
      hasJoinedCustomerProfile &&
      !hasJoinedTable &&
      table
    ) {
      const joinTablePayload = { tableId: table.id };
      retry(() =>
        socket.current
          .timeout(2000)
          .emitWithAck('table/join', joinTablePayload),
      ).then((msg) => {
        console.info(`Joined Table "${table.name}" (${msg})`, joinTablePayload);
        setHasJoinedTable(true);
      });
    }
  }, [isSocketConnected, hasJoinedCustomerProfile, hasJoinedTable, table]);

  // join-pickup-station process (for both merchants and customers)
  useEffect(() => {
    if (
      isSocketConnected &&
      (hasJoinedCustomerProfile || hasJoinedMerchantProfile) &&
      !hasJoinedPickupStation &&
      pickupStation
    ) {
      const joinPickupStationPayload = { pickupStationId: pickupStation.id };
      retry(() =>
        socket.current
          .timeout(2000)
          .emitWithAck('pickup-station/join', joinPickupStationPayload),
      ).then((msg) => {
        console.info(
          `Joined PickupStation "${pickupStation.name}" (${msg})`,
          joinPickupStationPayload,
        );
        setHasJoinedPickupStation(true);
      });
    }
  }, [
    isSocketConnected,
    hasJoinedCustomerProfile,
    hasJoinedMerchantProfile,
    hasJoinedPickupStation,
    pickupStation,
  ]);

  useEffect(() => {
    if (
      isSocketConnected &&
      (hasJoinedCustomerProfile || hasJoinedMerchantProfile) &&
      hasJoinedPickupStation &&
      !pickupStation
    ) {
      retry(() =>
        socket.current.timeout(2000).emitWithAck('pickup-station/leave'),
      ).then((msg) => {
        console.log(`Left PickupStation (${msg})`);
        setHasJoinedPickupStation(false);
      });
    }
  }, [
    isSocketConnected,
    hasJoinedCustomerProfile,
    hasJoinedMerchantProfile,
    hasJoinedPickupStation,
    pickupStation,
  ]);

  useEffect(() => {
    if (
      isSocketConnected &&
      hasJoinedCustomerProfile &&
      hasJoinedTable &&
      !table
    ) {
      retry(() => socket.current.timeout(2000).emitWithAck('table/leave')).then(
        (msg) => {
          console.log(`Left Table (${msg})`);
          setHasJoinedTable(false);
        },
      );
    }
  }, [isSocketConnected, hasJoinedCustomerProfile, hasJoinedTable, table]);

  // share cart everytime it changes
  useEffect(() => {
    if (table && checkout.checkoutMethod !== CHECKOUT_METHOD_PAY_AND_GO) {
      // there is a new item or item deleted from cart
      if (cart?.length !== cartQty.current) {
        cartQty.current = cart?.length;

        // only share cart when not empty
        if (cart?.length > 0) {
          if (hasJoinedTable && hasJoinedCustomerProfile) {
            const ownCart = { items: convertCartToOrderItems(cart) };

            retry(() =>
              socket.current
                .timeout(2000)
                .emitWithAck('table/shared-carts', ownCart),
            ).then((msg) => {
              console.info(`Shared own Cart (${msg})`, ownCart);
            });
          } else {
            console.error(
              'Cannot share your Cart as you have not joined a Table!',
            );
          }
        }
      }
    }
  }, [table, hasJoinedTable, hasJoinedCustomerProfile, cart, me]);

  const handleEmitSelectOrderItem = (selectionId, mode) => {
    if (hasJoinedTable && hasJoinedCustomerProfile) {
      retry(() =>
        socket.current
          .timeout(2000)
          .emitWithAck('table/cart', { selectionId, mode }),
      ).then((msg) => {
        console.info(`Shared own Cart (${msg})`);
      });
    } else {
      console.error('Cannot share your Cart as you have not joined a Table!');
    }
  };

  return (
    <Context.Provider
      value={{
        socket: socket.current,
        setToken,
        handleEmitSelectOrderItem,
      }}
    >
      {children}
    </Context.Provider>
  );
};

const mapStateToProps = (state) => ({
  me: state.me.me,
  table: state.store.table,
  pickupStation: state.store.pickupStation,
  cart: state.store.cart,
  checkout: state.store.checkout,
  order: state.store.order,
  sharedCarts: state.store.sharedCarts,
  orders: state.me.orders,
  storeOrders: state.store.storeOrders,
  processedSocketEventIds: state.store.processedSocketEventIds,
  store: state.store.store,
});

export const SocketContext = Context;

export const SocketProvider = connect(mapStateToProps, {
  setTableCart,
  setSpecificOrder,
  setStoreSpecificOrder,
  setSharedCarts,
  setOrderInfoModalData,
  setIsOrderInfoModalOpen,
  setOrder,
  addProcessedSocketEventId,
})(SocketProviderWrapper);
